/**
 * THE ACTION EXECUTION IDENTITY (§11) — the one way `players.anim` is written,
 * and the one place a new execution is stamped.
 *
 * ── The problem this solves ────────────────────────────────────────────────
 *
 * `AnimState` is a LEVEL. `AnimState.Pass` begins, and the sim then HOLDS it for
 * KICK_ANIM_HOLD_S (~0.22 s, 13 ticks) so the swing has time to be drawn. Every
 * render frame and every snapshot inside that window sees the same value. So a
 * downstream consumer — the animation director, a contact-event tracker, a VFX
 * hook — is asked to answer "is this the same pass I already saw, or a new one?"
 * from a field that cannot express the difference.
 *
 * The two obvious answers are both wrong:
 *
 *   - dedupe on the VALUE: two passes in a row are one pass. A striker who wins
 *     the ball back and plays it again immediately animates once.
 *   - dedupe on the FRAME or TICK NUMBER: the tick is not stable. Reconciliation
 *     (§2.4) re-simulates the same ticks with corrected inputs, a paused or
 *     throttled renderer repeats one, and the interpolation view replays ticks
 *     it already showed. Every one of those re-fires the event.
 *
 * `animExecId` is the identity: a per-player counter bumped exactly once when
 * the sim ACCEPTS an action. Two passes are two ids. A held state is one id for
 * its whole life. A re-simulated tick restores the id it had, because the field
 * is cloned and hashed like all sim state (world.ts).
 *
 * ── Purity (README, D1/D2) ─────────────────────────────────────────────────
 *
 * A monotonic integer increment over existing sim state. No clock, no RNG, no
 * allocation. `(x + 1) | 0` wraps at INT32 rather than leaving the integer range
 * — 2^31 executions is ~40 years of continuous passing at one per tick, and the
 * wrap is deterministic either way. Consumers compare for INEQUALITY only, never
 * ordering, so a wrap is invisible to them.
 *
 * ── Why every anim write goes through here ─────────────────────────────────
 *
 * Before this module, `players.anim` was assigned in fifteen places across five
 * files, some with an "if it changed" guard and some without (the keeper
 * re-stamps his dive pose EVERY tick — see reassertDiveAnim). An id maintained
 * beside those assignments would drift out of step with them within a session.
 * `setAnimState` is the guard, the blend reset and the stamp in one call, so
 * "the state changed" and "a new execution began" cannot disagree.
 */

import { AnimState, type PlayerArrays } from './types.js';

/**
 * Bits the execution id is quantized to for the wire (net/snapshot.ts).
 *
 * FOUR IS ENOUGH, and the reason is worth stating because "16 values for an
 * identity" looks alarming. The consumer never orders these and never treats one
 * as a handle to look anything up — it asks a single question, "is this a
 * different execution from the one I am already running?" A wrap can only lie if
 * EXACTLY 2^4 executions land between two consecutive observations of the same
 * player. The shortest action in ACTION_DEFS is 8 ticks and snapshots go out at
 * SNAPSHOT_SEND_RATE, so sixteen of them cannot fit in one snapshot interval —
 * and if they somehow did, the interleaved `anim` values would have changed too.
 * The alternative (32 bits × 22 players) buys nothing and costs real bandwidth.
 */
export const ANIM_EXEC_WIRE_BITS = 4;
const ANIM_EXEC_WIRE_MASK = (1 << ANIM_EXEC_WIRE_BITS) - 1;

/**
 * The wire/render token for an execution id. Both the LOCAL predicted player
 * (full int, straight off WorldState) and REMOTE players (already wrapped by the
 * snapshot codec) must be reduced to the same space before the renderer compares
 * them, or switching which player you control would look like a new execution.
 */
export function animExecToken(execId: number): number {
  return execId & ANIM_EXEC_WIRE_MASK;
}

/**
 * Stamp a NEW execution on `id` without touching `anim`.
 *
 * For the RE-ARM case, which is the whole reason the id exists: the sim accepts
 * an action while the SAME `AnimState` is already showing. A second pass inside
 * the first pass's hold window writes `anim = Pass` over `anim = Pass` — no edge,
 * nothing for a value-comparing consumer to see — but it is unambiguously a
 * second execution and must animate, contact and recover as one.
 *
 * Call this at the point the action is ACCEPTED (the impulse is committed / the
 * challenge is armed), never at the point it is merely requested — a rejected
 * input must not consume an id, or "one animation start per accepted action"
 * becomes "per attempted action".
 */
export function beginAnimExec(p: PlayerArrays, id: number): void {
  p.animExecId[id] = (p.animExecId[id]! + 1) | 0;
}

/**
 * Set `anim`, and if the value CHANGED reset the blend and stamp a new
 * execution. Idempotent when the state is unchanged — safe to call every tick,
 * which is what the keeper's dive re-assert and the strike hold both do.
 *
 * A locomotion transition (Run → Idle) gets an execution id too. That is
 * deliberate: the director's lifecycle bookkeeping is uniform over commands, and
 * a sustained state genuinely IS one execution of that state — it is what lets
 * "one recovery lifecycle per execution" hold for a fall as well as for a pass.
 */
export function setAnimState(p: PlayerArrays, id: number, next: AnimState): void {
  if (p.anim[id]! === next) return;
  p.anim[id] = next;
  p.animBlend[id] = 0;
  beginAnimExec(p, id);
}

/**
 * Set `anim` and stamp a new execution UNCONDITIONALLY — the accepted-action
 * form of `setAnimState`.
 *
 * Used where reaching the call site is itself proof the sim accepted a discrete
 * action: ball.ts after the impulse is applied, the tackle/skill/jump starts, the
 * keeper's dive commit, the Law 15 throw-in release. In every one of those the
 * state may or may not already be showing, and the answer must not change the
 * outcome.
 */
export function beginAnimAction(p: PlayerArrays, id: number, next: AnimState): void {
  p.anim[id] = next;
  p.animBlend[id] = 0;
  beginAnimExec(p, id);
}
